"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Container,
  Typography,
  Box,
  List,
  ListItemText,
  Radio,
  FormControlLabel,
  ListItemButton,
  Avatar,
  Stack,
  CircularProgress,
  Chip,
  Divider,
  Alert,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { useNavigate, useOutletContext } from "react-router-dom"
import PropertyFooterLayout from "../../../components/properties/PropertyFooterLayout"
import { getAllRealtors } from "../../../services/realtorService"
import { useAuth } from "../../../context/AuthContext"
import GroupIcon from "@mui/icons-material/Group"
import PersonIcon from "@mui/icons-material/Person"

const progress = 60
const step = 3

// Interfaz para los realtors que vienen de la API
interface Realtor {
  realtorId: number
  fullName: string
  profileImage: string | null
  teamId: number | null
  teamName: string | null
  isLeader: boolean | null
  phoneNumber: string
  contactEmail: string
  code: string | null
}

// Interfaz para el contexto
type PublishOutletContext = {
  handleNavigateToPropertyResume: () => void
  selectedRealtorId: number | null
  setSelectedRealtorId: (id: number) => void
}

const RealtorSelectionPage: React.FC = () => {
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [realtors, setRealtors] = useState<Realtor[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [selectedRealtor, setSelectedRealtor] = useState<Realtor | null>(null)
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const navigate = useNavigate()
  const { userToken } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const { handleNavigateToPropertyResume, selectedRealtorId, setSelectedRealtorId } =
    useOutletContext<PublishOutletContext>()

  // Verificar si el usuario es administrador
  useEffect(() => {
    if (userToken) {
      const adminProfile = userToken.user.profiles.find((profile) => profile.roleId === 8)
      setIsAdmin(!!adminProfile) // Convertir a booleano, será true solo si existe adminProfile
      console.log(
        "¿Es administrador?",
        !!adminProfile,
        "Perfiles:",
        userToken.user.profiles.map((p) => p.roleId),
      )
    }
  }, [userToken])

  // Cargar todos los realtors al montar el componente
  useEffect(() => {
    const fetchRealtors = async () => {
      if (userToken) {
        try {
          setLoading(true)
          const realtorsData = await getAllRealtors(userToken.hashedKey)

          if (Array.isArray(realtorsData)) {
            setRealtors(realtorsData)

            // Si ya hay un realtor seleccionado, mantenerlo
            if (selectedRealtorId) {
              setSelectedId(selectedRealtorId)
              const selected = realtorsData.find((r) => r.realtorId === selectedRealtorId)
              if (selected) {
                setSelectedRealtor(selected)
              }
            }
          }
        } catch (error) {
          console.error("Error al obtener realtors:", error)
          console.log("Token usado:", localStorage.getItem("authToken"))
        } finally {
          setLoading(false)
        }
      }
    }

    fetchRealtors()
  }, [userToken, selectedRealtorId])

  const handleSelection = (realtor: Realtor) => {
    setSelectedId(realtor.realtorId)
    setSelectedRealtor(realtor)
    setSelectedRealtorId(realtor.realtorId)
  }

  const handleBack = () => {
    navigate(-1)
  }

  const handleContinue = () => {
    // Verificar que el ID del realtor esté seleccionado
    if (!selectedId) {
      console.error("No se ha seleccionado ningún realtor")
      return
    }

    // Guardar el ID del realtor seleccionado
    setSelectedRealtorId(selectedId)

    handleNavigateToPropertyResume()
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ display: "flex", pb: 12, flexDirection: "column", alignItems: "center" }}>
        <Box p={4} sx={{ width: "80%" }}>
          <Alert severity="warning">
            No tienes permisos para acceder a esta sección. Solo los administradores pueden asignar realtors.
          </Alert>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button variant="contained" color="primary" onClick={handleBack}>
              Volver atrás
            </Button>
          </Box>
        </Box>
      </Container>
    )
  }
  return (
    <Container maxWidth="md" sx={{ display: "flex", pb: 12, flexDirection: "column", alignItems: "center" }}>
      <Box p={4} sx={{ width: "100%" }}>
        <Typography variant="h4" gutterBottom mb={2} sx={{ fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" } }}>
          Seleccionar Agente Inmobiliario
        </Typography>
        <Typography variant="body1" mb={4}>
          Selecciona el agente inmobiliario que se encargará de esta propiedad.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
            <CircularProgress sx={{ color: "green" }} />
          </Box>
        ) : realtors.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
            <Typography variant="body1" color="textSecondary">
              No se encontraron agentes inmobiliarios. Por favor, inténtalo de nuevo más tarde.
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ width: "100%" }}>
              {realtors.map((realtor) => (
                <ListItemButton
                  key={realtor.realtorId}
                  onClick={() => handleSelection(realtor)}
                  sx={{
                    mb: 2,
                    p: { xs: 1, sm: 2 },
                    borderRadius: 2,
                    border: selectedId === realtor.realtorId ? "2px solid black" : "1px solid grey",
                    transition: "border 0.3s",
                    cursor: "pointer",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Radio
                        checked={selectedId === realtor.realtorId}
                        value={realtor.realtorId}
                        color="success"
                        sx={{ p: { xs: 0.5, sm: 1 } }}
                      />
                    }
                    label={
                      <Stack
                        direction={isMobile ? "column" : "row"}
                        spacing={isMobile ? 1 : 2}
                        alignItems={isMobile ? "flex-start" : "center"}
                        sx={{ width: "100%" }}
                      >
                        <Avatar
                          src={realtor.profileImage || ""}
                          sx={{
                            width: { xs: 40, sm: 56 },
                            height: { xs: 40, sm: 56 },
                            alignSelf: isMobile ? "center" : "flex-start",
                          }}
                        />
                        <Box sx={{ flexGrow: 1, width: "100%" }}>
                          <ListItemText
                            primary={realtor.fullName}
                            secondary={realtor.code || realtor.phoneNumber}
                            primaryTypographyProps={{
                              variant: "h6",
                              fontWeight: "bold",
                              fontSize: { xs: "0.9rem", sm: "1rem", md: "1.25rem" },
                            }}
                            secondaryTypographyProps={{
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                          />
                          {realtor.teamId && (
                            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap" }}>
                              <Chip
                                icon={realtor.isLeader ? <GroupIcon /> : <PersonIcon />}
                                label={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.8rem" },
                                      whiteSpace: "normal",
                                      display: "flex",
                                      flexWrap: "wrap",
                                    }}
                                  >
                                    {realtor.teamName}{" "}
                                    <Box
                                      component="span"
                                      sx={{
                                        ml: { xs: 0, sm: 0.5 },
                                        display: { xs: "block", sm: "inline" },
                                      }}
                                    >
                                      {realtor.isLeader ? "(Líder)" : "(Miembro)"}
                                    </Box>
                                  </Typography>
                                }
                                color="primary"
                                size="small"
                                sx={{
                                  height: "auto",
                                  "& .MuiChip-label": {
                                    display: "block",
                                    whiteSpace: "normal",
                                    py: 0.5,
                                  },
                                  maxWidth: "100%",
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Stack>
                    }
                    sx={{ width: "100%", m: 0 }}
                  />
                </ListItemButton>
              ))}
            </List>

            {selectedRealtor && (
              <Box
                mt={4}
                p={{ xs: 2, sm: 3 }}
                sx={{ border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "#f9f9f9" }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                  Agente Seleccionado
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={2}
                  alignItems={isMobile ? "center" : "flex-start"}
                >
                  <Avatar
                    src={selectedRealtor.profileImage || ""}
                    sx={{ width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 } }}
                  />
                  <Box sx={{ textAlign: isMobile ? "center" : "left", width: "100%" }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                      {selectedRealtor.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRealtor.contactEmail}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRealtor.phoneNumber}
                    </Typography>
                    {selectedRealtor.teamId && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          justifyContent: isMobile ? "center" : "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          icon={selectedRealtor.isLeader ? <GroupIcon /> : <PersonIcon />}
                          label={
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                                whiteSpace: "normal",
                              }}
                            >
                              {selectedRealtor.teamName} {selectedRealtor.isLeader ? "(Líder)" : "(Miembro)"}
                            </Typography>
                          }
                          color="primary"
                          sx={{
                            height: "auto",
                            "& .MuiChip-label": {
                              display: "block",
                              whiteSpace: "normal",
                              py: 0.5,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Box>

      <PropertyFooterLayout
        progress={progress}
        step={step}
        onBack={handleBack}
        onContinue={handleContinue}
        isContinueDisabled={!selectedId}
      />
    </Container>
  )
}

export default RealtorSelectionPage
